import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Empty, Image, Spin, Tag } from "antd";
import { LockOutlined } from "@ant-design/icons";
import GoogleMapReact from "google-map-react";
import moment from "moment";

import { FILES_URL, apiBaseUrl } from "../../../config/config";
import { KEY_GOOGLE_MAPS } from "../../../config/constants";

import "./OpenModalPCI.css";

const getSiteName = (site) =>
  (site?.accountName || site?.companyName || site?.leadName || "").toString();

const getPciHistory = (entity) =>
  Array.isArray(entity?.PCIHistory) ? entity.PCIHistory : [];

const getLatestHistory = (entity) => {
  const history = getPciHistory(entity);
  return history.length ? history[history.length - 1] : null;
};

const getPciScore = (entity) => {
  const latest = getLatestHistory(entity);
  const rawScore =
    entity?.PCIValue !== null && typeof entity?.PCIValue !== "undefined"
      ? entity.PCIValue
      : latest?.finalScore;
  const score = Number(rawScore);

  if (!Number.isFinite(score) || score < 0) {
    return null;
  }

  return Math.round(score);
};

const getNumericPciScore = (value) => {
  const score = Number(value);

  return Number.isFinite(score) && score >= 0 ? score : null;
};

const getProcessPciScore = (process) => {
  const payload = process?.responsePayload;
  const result = payload?.result || payload?.data || payload || {};
  const keys = [
    "finalScore",
    "final_score",
    "pciScore",
    "pci_score",
    "score",
    "PCIValue",
  ];

  for (let index = 0; index < keys.length; index += 1) {
    const score = getNumericPciScore(result[keys[index]]);
    if (score !== null) {
      return score;
    }
  }

  return getNumericPciScore(process?.finalScore);
};

const getSectionPciScore = (section, sectionIndex, historyIndex, processes) => {
  const completedProcesses = (Array.isArray(processes) ? processes : []).filter(
    (process) =>
      Number(process?.historyIndex) === historyIndex &&
      Number(process?.sectionIndex) === sectionIndex &&
      String(process?.status || "").toLowerCase() === "completed",
  );

  for (let index = 0; index < completedProcesses.length; index += 1) {
    const score = getProcessPciScore(completedProcesses[index]);
    if (score !== null) {
      return score;
    }
  }

  return getNumericPciScore(section?.score);
};

const formatDate = (date) => {
  if (!date) {
    return "-";
  }

  const parsed = moment(date);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "-";
};

const getStatusTagColor = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (normalizedStatus === "finished" || normalizedStatus === "done") {
    return "green";
  }

  if (normalizedStatus === "inprogress" || normalizedStatus === "pending") {
    return "gold";
  }

  if (normalizedStatus === "failed" || normalizedStatus === "error") {
    return "red";
  }

  return "default";
};

const resolveFileUrl = (filePath) => {
  const value = String(filePath || "").trim();

  if (!value) {
    return "";
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const normalizedPath = value.replace(/^\/+/, "");

  if (/^uploaded\//i.test(normalizedPath)) {
    return `${apiBaseUrl}/${normalizedPath}`;
  }

  if (/^files\//i.test(normalizedPath)) {
    return `${apiBaseUrl}/uploaded/${normalizedPath}`;
  }

  return `${FILES_URL}/${normalizedPath}`;
};

const getSectionImages = (section) =>
  Array.isArray(section?.imageList) ? section.imageList : [];

const getImageFileName = (image) => image?.filename || image?.fileName || image;

const getImageCoordinates = (image) => {
  const lat = Number(image?.position?.lat);
  const lng = Number(image?.position?.lng);

  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    return null;
  }

  return { lat, lng };
};

const getLocatedSectionImages = (sections) => {
  const seen = new Set();
  const images = [];

  sections.forEach((section, sectionIndex) => {
    getSectionImages(section).forEach((image, imageIndex) => {
      const coordinates = getImageCoordinates(image);
      const url = resolveFileUrl(getImageFileName(image));
      const key = `${url}-${coordinates?.lat}-${coordinates?.lng}`;

      if (!coordinates || !url || seen.has(key)) {
        return;
      }

      seen.add(key);
      images.push({
        coordinates,
        url,
        fileName: String(getImageFileName(image) || "PCI image"),
        sectionName: `Section ${sectionIndex + 1}`,
        imageNumber: imageIndex + 1,
      });
    });
  });

  return images;
};

const getLocationGroups = (images) => {
  const groups = new Map();

  images.forEach((image) => {
    const key = `${image.coordinates.lat.toFixed(7)},${image.coordinates.lng.toFixed(7)}`;
    const group = groups.get(key) || {
      coordinates: image.coordinates,
      images: [],
    };

    group.images.push(image);
    groups.set(key, group);
  });

  return Array.from(groups.values());
};

const createInfoWindowContent = (location) => {
  const content = document.createElement("div");
  const title = document.createElement("strong");
  const coordinates = document.createElement("small");
  const gallery = document.createElement("div");

  content.className = "pci-modal__map-info";
  title.textContent = `${location.images.length} PCI photo${
    location.images.length === 1 ? "" : "s"
  } at this location`;
  coordinates.textContent = `Lat ${location.coordinates.lat} | Lng ${location.coordinates.lng}`;
  gallery.className = "pci-modal__map-info-gallery";

  location.images.forEach((image) => {
    const previewLink = document.createElement("a");
    const preview = document.createElement("img");

    previewLink.href = image.url;
    previewLink.target = "_blank";
    previewLink.rel = "noopener noreferrer";
    preview.title = `${image.sectionName} - Photo ${image.imageNumber}`;
    preview.src = image.url;
    preview.alt = image.fileName;
    previewLink.appendChild(preview);
    gallery.appendChild(previewLink);
  });

  content.append(title, coordinates, gallery);

  return content;
};

function PciPhotoMap({ locations }) {
  const [isMapReady, setIsMapReady] = useState(false);
  const mapRef = useRef(null);
  const mapsRef = useRef(null);
  const markersRef = useRef([]);

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => {
      marker.setMap(null);
    });
    markersRef.current = [];
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const maps = mapsRef.current;

    if (!isMapReady || !map || !maps || !locations.length) {
      return undefined;
    }

    clearMarkers();
    const infoWindow = new maps.InfoWindow();
    const bounds = new maps.LatLngBounds();

    locations.forEach((location) => {
      const marker = new maps.Marker({
        map,
        position: location.coordinates,
        label: String(location.images.length),
        title: `${location.images.length} PCI photo${
          location.images.length === 1 ? "" : "s"
        }`,
      });

      marker.addListener("click", () => {
        infoWindow.setContent(createInfoWindowContent(location));
        infoWindow.open(map, marker);
      });

      markersRef.current.push(marker);
      bounds.extend(location.coordinates);
    });

    if (locations.length === 1) {
      map.setCenter(locations[0].coordinates);
      map.setZoom(18);
    } else {
      map.fitBounds(bounds);
    }

    return () => {
      infoWindow.close();
      clearMarkers();
    };
  }, [clearMarkers, isMapReady, locations]);

  const handleGoogleApiLoaded = useCallback(({ map, maps }) => {
    mapRef.current = map;
    mapsRef.current = maps;
    setIsMapReady(true);
  }, []);

  return (
    <div className="pci-modal__map">
      <GoogleMapReact
        bootstrapURLKeys={{ key: KEY_GOOGLE_MAPS }}
        defaultCenter={locations[0].coordinates}
        defaultZoom={17}
        options={{
          mapTypeId: "satellite",
          mapTypeControl: true,
          streetViewControl: false,
        }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleGoogleApiLoaded}
      />
    </div>
  );
}

function PciSectionText({ label, text }) {
  if (!text) {
    return null;
  }

  return (
    <details className="pci-modal__section-detail">
      <summary>{label}</summary>
      <p>{text}</p>
    </details>
  );
}

export function OpenModalPCI({ site, data, isLoading, error, locked }) {
  const entity = data?.entity || site || {};
  const processes = Array.isArray(data?.processes) ? data.processes : [];

  const latestHistory = getLatestHistory(entity);
  const score = getPciScore(entity);
  const historyIndex = getPciHistory(entity).findIndex(
    (history) => history === latestHistory,
  );
  const sections = useMemo(
    () =>
      Array.isArray(latestHistory?.PCISections)
        ? latestHistory.PCISections
        : [],
    [latestHistory],
  );
  // Account.coverPic is the account's own "profile picture" (mirrors the
  // latest PCI's cover, see pci-process.server.service.js), so it's the
  // preferred source; falling back to the history entry's own PCICoverPic
  // covers accounts whose coverPic hasn't been backfilled yet.
  const coverImageUrl = resolveFileUrl(entity?.coverPic || latestHistory?.PCICoverPic);
  const locatedImages = useMemo(
    () => getLocatedSectionImages(sections),
    [sections],
  );
  const locationGroups = useMemo(
    () => getLocationGroups(locatedImages),
    [locatedImages],
  );

  console.log("locatedImages", locatedImages);

  if (locked) {
    return (
      <div className="pci-modal__empty pci-modal__locked">
        <LockOutlined className="pci-modal__locked-icon" />
        <p className="pci-modal__locked-text">
          This content is reserved for PARC and PARC360 protected sites.
          Please contact PotholeRepair.com at 855.376.8465 or
          info@potholerepair.com for assistance or to request coverage for
          this site.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="pci-modal__loading">
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pci-modal__empty">
        <Empty description={error} />
      </div>
    );
  }

  if (!latestHistory && score === null && !processes.length) {
    return (
      <div className="pci-modal__empty">
        <Empty description="No PCI information available" />
      </div>
    );
  }

  return (
    <div className="pci-modal">
      <div className="pci-modal__summary">
        <div className="pci-modal__score">
          <div className="pci-modal__score-number">
            {score === null ? "--" : score}
          </div>
          <div className="pci-modal__score-label">Current PCI</div>
        </div>

        <div className="pci-modal__details">
          <h3 className="pci-modal__title">{getSiteName(entity || site)}</h3>
          <div className="pci-modal__meta">
            <div className="pci-modal__meta-item">
              <span className="pci-modal__meta-label">Status</span>
              <span className="pci-modal__meta-value">
                <Tag color={getStatusTagColor(entity?.PCIStatus)}>
                  {entity?.PCIStatus || "-"}
                </Tag>
              </span>
            </div>
            <div className="pci-modal__meta-item">
              <span className="pci-modal__meta-label">Expires</span>
              <span className="pci-modal__meta-value">
                {formatDate(entity?.PCIValueExpirationDate)}
              </span>
            </div>
            <div className="pci-modal__meta-item">
              <span className="pci-modal__meta-label">Requested</span>
              <span className="pci-modal__meta-value">
                {formatDate(latestHistory?.dateRequested)}
              </span>
            </div>
            <div className="pci-modal__meta-item">
              <span className="pci-modal__meta-label">History</span>
              <span className="pci-modal__meta-value">
                {getPciHistory(entity).length} records
              </span>
            </div>
          </div>
        </div>
      </div>

      {coverImageUrl || locationGroups.length ? (
        <div
          className={`pci-modal__media-grid${
            coverImageUrl ? " pci-modal__media-grid--with-cover" : ""
          }`}
        >
          {coverImageUrl ? (
            <section className="pci-modal__media-card pci-modal__cover-card">
              <h4 className="pci-modal__section-title">Cover Image</h4>
              <Image
                className="pci-modal__cover"
                src={coverImageUrl}
                alt="PCI cover"
              />
            </section>
          ) : null}

          {locationGroups.length ? (
            <section className="pci-modal__media-card pci-modal__locations-card">
              <h4 className="pci-modal__section-title">
                Photo Locations
                <span className="pci-modal__section-title-meta">
                  {locatedImages.length} photos · {locationGroups.length} GPS {locationGroups.length === 1 ? "location" : "locations"}
                </span>
              </h4>
              <PciPhotoMap locations={locationGroups} />
            </section>
          ) : null}
        </div>
      ) : null}

      <h4 className="pci-modal__section-title">Sections</h4>
      {sections.length ? (
        <div className="pci-modal__sections">
          {sections.map((section, index) => {
            const images = getSectionImages(section);
            const sectionScore = getSectionPciScore(
              section,
              index,
              historyIndex,
              processes,
            );
            return (
              <div className="pci-modal__section" key={section?._id || index}>
                <div className="pci-modal__section-head">
                  <h5 className="pci-modal__section-name">
                    Section {index + 1}
                  </h5>
                  <Tag color={getStatusTagColor(section?.status)}>
                    {sectionScore !== null
                      ? `PCI ${sectionScore}`
                      : section?.status || "Pending"}
                  </Tag>
                </div>

                {section?.notes ? (
                  <p className="pci-modal__text">
                    <strong>Notes:</strong> {section.notes}
                  </p>
                ) : null}
                <PciSectionText label="Summary" text={section?.summary} />
                <PciSectionText label="Risks" text={section?.risks} />

                {Array.isArray(section?.issues) && section.issues.length ? (
                  <ul className="pci-modal__issues">
                    {section.issues.slice(0, 4).map((issue, issueIndex) => (
                      <li key={`${issue?.issueType || "issue"}-${issueIndex}`}>
                        {issue?.issueType || "Issue"}
                        {issue?.severity ? ` - ${issue.severity}` : ""}
                      </li>
                    ))}
                  </ul>
                ) : null}

                {images.length ? (
                  <Image.PreviewGroup>
                    <div className="pci-modal__gallery">
                      {images.map((image, imageIndex) => {
                        const url = resolveFileUrl(getImageFileName(image));
                        if (!url) {
                          return null;
                        }

                        return (
                          <Image
                            key={`${url}-${imageIndex}`}
                            className="pci-modal__thumb"
                            src={url}
                            alt={`PCI section ${index + 1}`}
                          />
                        );
                      })}
                    </div>
                  </Image.PreviewGroup>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <Empty description="No section detail available" />
      )}

      {processes.length ? (
        <>
          <h4 className="pci-modal__section-title">Process History</h4>
          <div className="pci-modal__processes">
            {processes.slice(0, 8).map((process) => (
              <div className="pci-modal__process" key={process?._id}>
                <div className="pci-modal__process-main">
                  <div className="pci-modal__process-title">
                    {process?.task || process?.groupKey || "PCI process"}
                  </div>
                  <div className="pci-modal__process-subtitle">
                    {formatDate(process?.createdAt)}
                    {process?.attempts ? ` - Attempts: ${process.attempts}` : ""}
                  </div>
                </div>
                <Tag color={getStatusTagColor(process?.status)}>
                  {process?.status || "-"}
                </Tag>
                {process?.finalScore !== null &&
                typeof process?.finalScore !== "undefined" ? (
                  <span className="pci-modal__meta-value">
                    PCI {Math.round(Number(process.finalScore))}
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
